import { SerializedConstructor } from '@langchain/core/load/serializable';

import DiffMatchPatch from 'diff-match-patch';
import isEqual from 'lodash/isEqual';

import {
  DIFF_DELETE_BG_COLOR,
  DIFF_INSERT_BG_COLOR,
} from '@/constants/diffConstants';
import { DiffChangeType } from '@/types/diff';
import {
  GetRepoResponse,
  MessageContentPart,
  MessageUnionType,
} from '@/types/schema';
import { PromptTool } from '@/utils/ai-tools';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';
import { getMessageContent } from '@/utils/messages';

import { StructuredPromptSchema } from '../Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';
import {
  getPromptManifest,
  isPromptPlaygroundManifest,
  isRunnablePrompt,
} from '../Playground/utils/Playground.utils';
import { MessageDiff } from './Commits/types';

export function diffString(
  originalString: string,
  newString: string
): DiffChangeType[] {
  const dmp = new DiffMatchPatch();

  const diffs = dmp.diff_main(originalString, newString);
  dmp.diff_cleanupSemantic(diffs);

  return diffs;
}

function diffMessage(
  previousMessage: string | MessageContentPart[],
  newMessage: string | MessageContentPart[]
) {
  if (typeof previousMessage === 'string' && typeof newMessage === 'string') {
    return diffString(previousMessage, newMessage);
  }

  if (isEqual(previousMessage, newMessage)) {
    return [[DiffMatchPatch.DIFF_EQUAL, newMessage]] as MessageDiff[];
  }

  return [
    [DiffMatchPatch.DIFF_DELETE, previousMessage],
    [DiffMatchPatch.DIFF_INSERT, newMessage],
  ] as MessageDiff[];
}

export function diffMessages(
  previousMessages: MessageUnionType[],
  currentMessages: MessageUnionType[]
): MessageDiff[][] {
  const previousMessageContents = previousMessages.map((message) =>
    getMessageContent(message)
  );
  const currentMessageContents = currentMessages.map((message) =>
    getMessageContent(message)
  );

  const isPreviousLonger =
    previousMessageContents.length > currentMessageContents.length;
  const toMap = isPreviousLonger
    ? previousMessageContents
    : currentMessageContents;
  const toCompare = isPreviousLonger
    ? currentMessageContents
    : previousMessageContents;
  const leftoverChangeValue = isPreviousLonger
    ? DiffMatchPatch.DIFF_DELETE
    : DiffMatchPatch.DIFF_INSERT;

  const diffs = toMap.map((message, index) => {
    const toCompareMessage = toCompare[index];

    if (!toCompareMessage)
      return [[leftoverChangeValue, message]] as MessageDiff[];

    if (!message)
      return [[-leftoverChangeValue, toCompareMessage]] as MessageDiff[];

    return diffMessage(
      isPreviousLonger ? message : toCompareMessage,
      isPreviousLonger ? toCompareMessage : message
    );
  });

  return diffs;
}

export function diffMessageTypes(
  previousTypes: string[],
  currentTypes: string[]
): DiffChangeType[][] {
  const isPreviousLonger = previousTypes.length > currentTypes.length;
  const toMap = isPreviousLonger ? previousTypes : currentTypes;
  const toCompare = isPreviousLonger ? currentTypes : previousTypes;
  const leftoverChangeValue = isPreviousLonger
    ? DiffMatchPatch.DIFF_DELETE
    : DiffMatchPatch.DIFF_INSERT;

  const diffs = toMap.map((type, index) => {
    const toCompareType = toCompare[index];
    if (!toCompareType)
      return [[leftoverChangeValue, type]] as DiffChangeType[];
    return diffString(type, toCompareType);
  });

  return diffs;
}

export function diffTools(
  previousTools: PromptTool[],
  currentTools: PromptTool[]
): DiffChangeType[][] {
  const [toMap, toCompare, leftoverChangeValue] =
    previousTools.length > currentTools.length
      ? [previousTools, currentTools, DiffMatchPatch.DIFF_DELETE]
      : [currentTools, previousTools, DiffMatchPatch.DIFF_INSERT];

  const diffs = toMap.map((tool, index) => {
    const toCompareTool = toCompare[index];
    if (!toCompareTool)
      return [
        [leftoverChangeValue, JSON.stringify(tool, null, 2)],
      ] as DiffChangeType[];
    return diffString(
      JSON.stringify(
        leftoverChangeValue === DiffMatchPatch.DIFF_DELETE
          ? tool
          : toCompareTool,
        null,
        2
      ),
      JSON.stringify(
        leftoverChangeValue === DiffMatchPatch.DIFF_DELETE
          ? toCompareTool
          : tool,
        null,
        2
      )
    ) as DiffChangeType[];
  });

  return diffs;
}

export const diffModelConfig = (
  previousModel?: SerializedConstructor,
  model?: SerializedConstructor
) => {
  const modelKwargs = model?.kwargs?.bound ?? model?.kwargs;
  const previousModelKwargs =
    previousModel?.kwargs?.bound ?? previousModel?.kwargs;

  if (!modelKwargs) {
    return [
      [
        DiffMatchPatch.DIFF_DELETE,
        JSON.stringify(previousModelKwargs, null, 2),
      ],
    ] as DiffChangeType[];
  } else if (!previousModelKwargs) {
    return [
      [DiffMatchPatch.DIFF_INSERT, JSON.stringify(modelKwargs, null, 2)],
    ] as DiffChangeType[];
  } else {
    return diffString(
      JSON.stringify(previousModelKwargs, null, 2),
      JSON.stringify(modelKwargs, null, 2)
    ) as DiffChangeType[];
  }
};

export const diffSchema = (
  previousSchema?: StructuredPromptSchema,
  currentSchema?: StructuredPromptSchema
) => {
  if (!previousSchema && !currentSchema) {
    return null;
  } else if (!previousSchema && currentSchema) {
    return [
      [DiffMatchPatch.DIFF_INSERT, JSON.stringify(currentSchema, null, 2)],
    ] as DiffChangeType[];
  } else if (!currentSchema && previousSchema) {
    return [
      [DiffMatchPatch.DIFF_DELETE, JSON.stringify(previousSchema, null, 2)],
    ] as DiffChangeType[];
  } else {
    return diffString(
      JSON.stringify(previousSchema, null, 2),
      JSON.stringify(currentSchema, null, 2)
    );
  }
};

export function getDiffColorFromChangeType(changeType: number) {
  switch (changeType) {
    case DiffMatchPatch.DIFF_INSERT:
      return DIFF_INSERT_BG_COLOR;
    case DiffMatchPatch.DIFF_DELETE:
      return DIFF_DELETE_BG_COLOR;
    default:
      return undefined;
  }
}

export function isChatPrompt(manifest: SerializedConstructor) {
  const msgs =
    isPromptPlaygroundManifest(manifest.id) || isRunnablePrompt(manifest.id)
      ? manifest.kwargs.first.kwargs.messages
      : manifest.kwargs.messages;
  return msgs !== undefined && msgs.length > 0;
}

export function getMessagesFromManifest(manifest: SerializedConstructor) {
  const promptManifest = getPromptManifest(manifest);

  if (isChatPrompt(promptManifest)) {
    return promptManifest.kwargs.messages;
  }

  return promptManifest.kwargs.template;
}

export interface UpstreamRepoUrlParams {
  repoObj?: GetRepoResponse;
  currentOwner?: string;
  inHub: boolean;
  organizationId: string | null;
}

export function getUpstreamRepoUrl({
  repoObj,
  currentOwner,
  inHub,
  organizationId: _organizationId,
}: UpstreamRepoUrlParams): string {
  if (!repoObj) {
    return '';
  }

  const upstreamRepoFullName = repoObj.repo?.upstream_repo_full_name;

  const [upstreamRepoOwner, upstreamRepoName] = upstreamRepoFullName?.includes(
    '/'
  )
    ? upstreamRepoFullName.split('/')
    : [null, null];

  const shouldUseHubPath =
    upstreamRepoOwner &&
    (upstreamRepoOwner !== (currentOwner ?? repoObj.repo.owner) ||
      (inHub && repoObj.repo.is_public));

  const basePath = shouldUseHubPath
    ? `${appHubIndexPath}/${upstreamRepoFullName}`
    : `${appPromptsIndexPath}/${
        repoObj.repo.owner ? upstreamRepoName : upstreamRepoFullName
      }`;

  return `/${basePath}`;
}
